import { useState } from 'react';
import { Building2, Check } from 'lucide-react';
import { BottomSheet } from '@/components/common/BottomSheet';
import { useI18n } from '@/lib/i18n';
import { cn } from '@/lib/utils';
import { hasAttendance, isOwnedBy } from '@/lib/workspace-select';
import type { Workspace } from '@/stores/workspace.store';

/**
 * Nama workspace aktif harus SELALU terlihat di tempat user absen (tiket Silvi/Voiz
 * 4 Agu 2026: clock-in diam-diam masuk ke workspace pribadinya, kantor tak pernah
 * melihat). Tiap pilihan diberi label "Absensi aktif / belum aktif" + "Milik kamu"
 * supaya workspace kantor vs pribadi mudah dibedakan.
 */

export function WorkspaceChips({ ws, userId }: { ws: Workspace; userId?: string | null }) {
    const { t } = useI18n();
    const on = hasAttendance(ws);
    return (
        <span className="flex flex-wrap items-center gap-1 mt-0.5">
            <span
                className={cn('text-[9px] font-bold px-1.5 py-0.5 rounded-full', on ? 'bg-brand-50 text-brand-700' : 'bg-gray-100 text-gray-500')}
            >
                {on ? t('workspace.chip_attendance_on') : t('workspace.chip_attendance_off')}
            </span>
            {isOwnedBy(ws, userId) && (
                <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800">
                    {t('workspace.chip_owned')}
                </span>
            )}
        </span>
    );
}

export function WorkspaceOptionList({ workspaces, currentId, userId, onSelect }: {
    workspaces: Workspace[];
    currentId?: string | null;
    userId?: string | null;
    onSelect: (ws: Workspace) => void;
}) {
    return (
        <div className="space-y-1">
            {workspaces.map((ws) => {
                const active = ws._id === currentId;
                return (
                    <button
                        key={ws._id}
                        type="button"
                        onClick={() => onSelect(ws)}
                        aria-current={active ? 'true' : undefined}
                        className={cn(
                            'flex items-center gap-2.5 w-full px-3 py-2 rounded-lg text-left transition',
                            active ? 'bg-brand/10' : 'hover:bg-gray-50',
                        )}
                    >
                        <Building2 className={cn('h-4 w-4 shrink-0', active ? 'text-brand-700' : 'text-gray-400')} />
                        <span className="flex-1 min-w-0">
                            <span className={cn('block truncate text-xs font-bold', active ? 'text-brand-700' : 'text-gray-800')}>{ws.name}</span>
                            <WorkspaceChips ws={ws} userId={userId} />
                        </span>
                        {active && <Check className="h-4 w-4 shrink-0 text-brand-700" />}
                    </button>
                );
            })}
        </div>
    );
}

/** Baris "Absen tercatat di workspace X" + tombol Ganti (kalau > 1 workspace). */
export function ActiveWorkspaceBar({ workspace, workspaces, userId, onSwitch, disabled }: {
    workspace: Workspace;
    workspaces: Workspace[];
    userId?: string | null;
    onSwitch: (ws: Workspace) => void;
    disabled?: boolean;
}) {
    const { t } = useI18n();
    const [open, setOpen] = useState(false);
    const canSwitch = workspaces.length > 1;
    // Milik sendiri padahal user juga anggota workspace orang lain (kantor) → ingatkan.
    const ownedWhileEmployed = isOwnedBy(workspace, userId) && workspaces.some((w) => !isOwnedBy(w, userId));

    return (
        <>
            <div className="flex items-center gap-2.5 mb-3 px-3 py-2 rounded-xl" style={{ backgroundColor: '#f6f2ed' }}>
                <Building2 className="h-4 w-4 shrink-0 text-gray-500" />
                <div className="flex-1 min-w-0">
                    <p className="text-[10px] text-gray-500 leading-none">{t('workspace.recorded_in')}</p>
                    <p className="text-sm font-black text-gray-900 truncate mt-0.5" data-testid="active-workspace-name">{workspace.name}</p>
                    {ownedWhileEmployed && (
                        <p className="text-[10px] text-amber-700 leading-tight mt-0.5">{t('workspace.owned_hint')}</p>
                    )}
                </div>
                {canSwitch && (
                    <button
                        type="button"
                        onClick={() => setOpen(true)}
                        disabled={disabled}
                        className="shrink-0 text-[11px] font-bold text-brand-700 px-3 py-1.5 rounded-full bg-white disabled:opacity-50"
                        style={{ border: '1px solid #e0d6cc' }}
                    >
                        {t('workspace.change')}
                    </button>
                )}
            </div>
            {canSwitch && (
                <BottomSheet open={open} onOpenChange={setOpen} title={t('workspace.switch')} subtitle={t('workspace.switch_hint')}>
                    <WorkspaceOptionList
                        workspaces={workspaces}
                        currentId={workspace._id}
                        userId={userId}
                        onSelect={(ws) => {
                            setOpen(false);
                            if (ws._id !== workspace._id) onSwitch(ws);
                        }}
                    />
                </BottomSheet>
            )}
        </>
    );
}
